import { useLocalStorageState } from 'ahooks';
import { isBoolean, isEmpty, isUndefined } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tCreateWorkspaceModalForm } from '../../pages/Auth/CreateWorkspace/CreateWorkspace.types';
import { tFunnelInfo } from '../../components/Chat/Chat.types';
import {
  tWorkspaceUpdatedEvent,
  tWorkspaceUsersUpdatedEvent,
  tWorkspaceUserUpdatedEvent,
} from '../SocketsProvider/Sockets.types';
import { eAlertType } from '../ToastProvider/Toast.enum';
import { useToastContext } from '../ToastProvider/Toast.hook';
import { useUserContext } from '../UserProvider/User.context';
import { eUserRole, eUserStateType } from '../UserProvider/User.enums';
import { tSessionUser } from '../UserProvider/User.types';
import { useAnalyticsAuth } from '../../hooks/analytics/auth/useAnalyticsAuth.hook';
import { WorkspaceService } from './Workspace.service';
import {
  applyWorkspaceUsersUpdateToFunnelData,
  buildWorkspaceInfo,
  findListedRole,
  isRoleResolved,
  normalizeWorkspaceInfo,
} from './Workspace.helpers';
import {
  tAddWorkspaceUserPayload,
  tAddWorkspaceUserResponse,
  tCurrentWorkspace,
  tCurrentWorkspaceInfo,
  tToggleUserInboxPayload,
  tUnlinkUserWorkspacePayload,
  tUpdateWorkspaceProps,
  tUpdateWorkspaceResponse,
  tWorkspaceContext,
  tWorkspaceRequest,
  tWorkspaceResponse,
  tWorkspacesResponse,
  tWorkspaceUserInfo,
  tResendInvitationPayload,
  tResendInvitationResponse,
} from './Workspace.types';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import {
  ATTRIBUTION_STORAGE_KEY,
  captureAttribution,
  captureDirectAdAttribution,
  isAttributionFresh,
  readStoredAttribution,
  resolveAttributionWrite,
  type tWorkspaceAttribution,
} from './Workspace.attribution';

function useWorkspace(): tWorkspaceContext {
  const {
    currentUserId,
    setCurrentUserRole,
    userRoleSettlement,
    setUserRoleSettlement,
    currentUser,
    setCurrentUser,
  } = useUserContext();
  const { addAlert } = useToastContext();
  const { registerWorkspaceContext } = useAnalyticsAuth();
  const [searchParams] = useSearchParams();
  const salesSource = searchParams.get('salesSource');
  const [canSeeWapiIntegration, setCanSeeWapiIntegration] =
    useState<boolean>(false);
  const [canSendBulkMessages, setCanSendBulkMessages] =
    useState<boolean>(false);
  // RET-2339: workspace de la última petición de la excepción de WBS, y el
  // workspace para el que esa excepción RESPONDIÓ. Ver el efecto que las usa.
  const [wapiAccessLoadedForWorkspaceId, setWapiAccessLoadedForWorkspaceId] =
    useState<string | undefined>(undefined);

  const localStorageStageOptions = {
    defaultValue: undefined,
  };

  const [currentWorkspaceId, setCurrentWorkspaceId] = useLocalStorageState<
    string | undefined
  >('currentWorkspaceId', localStorageStageOptions);
  const wapiAccessRequestWorkspaceIdRef = useRef<string | undefined>(
    currentWorkspaceId,
  );

  const [currentWorkspace, setCurrentWorkspace] = useLocalStorageState<
    tCurrentWorkspace | undefined
  >('currentWorkspace', localStorageStageOptions);

  const [workspaceInfo, setWorkspaceInfo] = useState<
    tCurrentWorkspaceInfo | undefined
  >();
  // The workspace the loaded info describes. The id can change without a reload
  // (creating a workspace) while `workspaceInfo` still holds the previous one.
  const [workspaceInfoWorkspaceId, setWorkspaceInfoWorkspaceId] = useState<
    string | undefined
  >();
  // `isLoadingWorkspaceInfo` complements `workspaceInfo` so route guards can
  // tell "fetch in flight" apart from "fetch resolved but workspaceInfo still
  // undefined" (e.g., fetch failed, legacy workspace without info). AuthMiddleware
  // holds rendering on the former; the latter must fall through to avoid an
  // indefinite blank screen when the BFF is temporarily unavailable.
  // Seed from `!!currentWorkspaceId` to mirror `FeatureFlagsProvider.isLoadingFeatureFlags`
  // so the loading gate holds from the very first render when a workspace id is
  // already known (avoiding a single-frame window where the gate is false and
  // workspaceInfo is still undefined).
  const [isLoadingWorkspaceInfo, setIsLoadingWorkspaceInfo] =
    useState<boolean>(!!currentWorkspaceId);
  const [workspaceInfoLoadedForId, setWorkspaceInfoLoadedForId] =
    useState<string>();
  // Track recently manually updated users to prevent socket events from overwriting
  // Key: userAuthId, Value: timestamp of last manual update
  // Socket events that arrive within 5 seconds of a manual update will preserve the manual update
  const recentlyUpdatedUsersRef = useRef<Map<string, number>>(new Map());
  // Read after an await: the workspace can change while a save is pending.
  const latestCurrentWorkspaceIdRef = useRef(currentWorkspaceId);
  latestCurrentWorkspaceIdRef.current = currentWorkspaceId;

  // GRO-1266: neither store is seeded from the URL. ahooks' setter skips the
  // localStorage write when the value is `Object.is` the current state, so a
  // state seeded with the captured value turned the persisting effects below
  // into no-ops: salesSource and the attribution blob lived in memory only and
  // were lost whenever signup and workspace creation happened in different
  // visits. Starting from the stored value lets those effects persist them.
  const [workspaceSaleSource, setWorkspaceSaleSource] = useLocalStorageState<
    string | undefined
  >('workspaceSaleSource', {
    defaultValue: undefined,
  });

  // Signup attribution (mirrors the workspaceSaleSource pattern): captured
  // once per URL change, persisted as ONE atomic blob so it survives in-SPA
  // navigations and the Google sign-in redirect. Sentinel-gated inside
  // captureAttribution — undefined when the visit isn't a decorated register
  // link, so an existing stash is never overwritten by partial params.
  const capturedAttribution = useMemo(
    () => captureAttribution(searchParams),
    [searchParams],
  );

  // GRO-913: Meta ads deep-link straight to /auth/sign-up, so they carry real
  // utm_*/click IDs but none of the collector's decoration and never trip the
  // sentinel above. Referrer and pathname are passed in (not read inside) to
  // keep the capture pure and testable.
  const capturedDirectAd = useMemo(
    () =>
      captureDirectAdAttribution(searchParams, {
        referrer: document.referrer,
        pathname: window.location.pathname,
      }),
    [searchParams],
  );

  const [workspaceAttribution, setWorkspaceAttribution] = useLocalStorageState<
    tWorkspaceAttribution | undefined
  >(ATTRIBUTION_STORAGE_KEY, {
    defaultValue: undefined,
  });

  // BUG-534 U4: hoisted from raw `localStorage.setItem` in
  // `handleWorkspaceUserUpdated` so the SYNC_STORAGE_EVENT_NAME dispatch
  // reaches live consumers (`Chat.hook`, `ChatInfoAttributes`, `ChatMenu`).
  // Raw setItem bypasses ahooks' SYNC dispatcher (same anti-pattern as
  // INT-945). Without the setter, the assign-to dropdown stayed stale
  // until a page reload.
  //
  // `listenStorageChange: false` — only the setter is destructured, so the
  // receiver-side native `storage` listener and SYNC subscription are
  // wasted work that re-renders `WorkspaceProvider` on every `funnelData`
  // mutation (including the new BUG-534 MERGE writes from
  // `FunnelCacheInvalidationProvider`). The setter still fires
  // SYNC_STORAGE_EVENT_NAME on every write regardless of this flag, so
  // live consumers (`Chat.hook`, etc.) still see the update.
  const [, setFunnelData] = useLocalStorageState<tFunnelInfo[]>('funnelData', {
    defaultValue: [],
    listenStorageChange: false,
  });

  const [workspaces, setWorkspaces] = useState<tWorkspacesResponse>();

  useEffect(() => {
    if (salesSource) {
      setWorkspaceSaleSource(salesSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U19 preserves the existing effect trigger (KTD4).
  }, [salesSource]);

  useEffect(() => {
    // Precedence (collector wins; a direct-ad touch inherits `hutk`/first-touch
    // from the stash) lives in resolveAttributionWrite so it stays unit-tested.
    const nextAttribution = resolveAttributionWrite(
      { collector: capturedAttribution, directAd: capturedDirectAd },
      readStoredAttribution(),
    );

    if (nextAttribution) {
      // Always a fresh object from the capture helpers — safe for the ahooks
      // Object.is bail-out (localstorage-state-mutation learning).
      setWorkspaceAttribution(nextAttribution);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U19 preserves the existing effect trigger (KTD4).
  }, [capturedAttribution, capturedDirectAd]);

  // PostHog/analytics super-properties: workspace context attached to every event
  // ingested from this session. Re-fires when the user switches workspaces or when
  // workspaceInfo arrives from the BFF after a fresh login.
  // Info of another workspace is held back, so its details are never stamped
  // on the current workspace.
  useEffect(() => {
    registerWorkspaceContext(
      currentWorkspaceId,
      workspaceInfoWorkspaceId === currentWorkspaceId
        ? workspaceInfo
        : undefined,
    );
  }, [
    currentWorkspaceId,
    workspaceInfo,
    workspaceInfoWorkspaceId,
    registerWorkspaceContext,
  ]);

  // Helper function to deduplicate users by email, preferring LINKED state
  // This handles cases where invitation entries (INVITATION_SENT/PENDING) and
  // registered entries (LINKED) exist for the same email
  const deduplicateUsers = useCallback(
    (
      usersArray: tWorkspaceUserInfo[],
    ): {
      [key: string]: tWorkspaceUserInfo;
    } => {
      const users: { [key: string]: tWorkspaceUserInfo } = {};
      // Track users by email to detect duplicates
      const usersByEmail: { [email: string]: string } = {};

      usersArray.forEach((user) => {
        // Skip users without authId or email
        if (!user.authId || !user.email) {
          return;
        }

        const emailLower = user.email.toLowerCase();
        // Check if we already have a user with this email
        const existingAuthId = usersByEmail[emailLower];
        if (existingAuthId && existingAuthId !== user.authId) {
          // Duplicate user found - prefer the one with LINKED state
          const existingUser = users[existingAuthId];
          const isExistingLinked =
            existingUser?.state === eUserStateType.LINKED;
          const isExistingInvitation =
            existingUser?.state === eUserStateType.INVITATION_SENT ||
            existingUser?.state === eUserStateType.PENDING ||
            existingUser?.state === eUserStateType.INVITATION_EXPIRED;
          const isNewLinked = user.state === eUserStateType.LINKED;
          const isNewInvitation =
            user.state === eUserStateType.INVITATION_SENT ||
            user.state === eUserStateType.PENDING ||
            user.state === eUserStateType.INVITATION_EXPIRED;

          // Priority: LINKED > INVITATION_SENT/PENDING > other states
          if (isNewLinked && isExistingInvitation) {
            // New user is LINKED, existing is invitation - replace with LINKED
            delete users[existingAuthId];
            users[user.authId] = user;
            usersByEmail[emailLower] = user.authId;
          } else if (isNewLinked && !isExistingLinked) {
            // New user is LINKED, existing is not - replace with new user
            delete users[existingAuthId];
            users[user.authId] = user;
            usersByEmail[emailLower] = user.authId;
          } else if (isExistingLinked && isNewInvitation) {
            // Existing is LINKED, new is invitation - keep existing LINKED
            // Don't add the new invitation entry
          } else {
            // Both are same state or both non-LINKED - keep existing (first one wins)
          }
          return;
        }

        // No duplicate found, add user
        users[user.authId] = user;
        usersByEmail[emailLower] = user.authId;
      });

      return users;
    },
    [],
  );

  useEffect(() => {
    // Creating a workspace changes the id without a reload, so the previous
    // workspace's answer can land after this run was cleaned up; this effect's
    // writes after the fetch are dropped then, or they would describe the new
    // workspace. `getAllWorkspaces` writes user-scoped state and is not guarded.
    let cancelled = false;
    setWorkspaceInfoLoadedForId(undefined);
    // A role settled for another workspace must not satisfy the identify gate
    // while this workspace's lookup is pending.
    setUserRoleSettlement((settlement) =>
      settlement?.workspaceId === currentWorkspaceId ? settlement : undefined,
    );

    (async () => {
      if (!currentWorkspaceId) {
        // A run cleaned up mid-fetch skips its `finally`, so the flag it set
        // is cleared here when no workspace is left to load.
        setIsLoadingWorkspaceInfo(false);
        return;
      }

      setIsLoadingWorkspaceInfo(true);
      try {
        // Check localStorage first for immediate availability (from activation flow)
        const cachedWorkspaceInfo = localStorage.getItem('workspaceInfo');
        if (cachedWorkspaceInfo) {
          try {
            const {
              workspaceId: cachedWorkspaceId,
              ...parsedInfo
            }: tCurrentWorkspaceInfo & { workspaceId?: unknown } =
              JSON.parse(cachedWorkspaceInfo);
            // A blob of another workspace, or one without an id, waits for the
            // fetch: its details would be registered for the wrong workspace.
            if (
              typeof cachedWorkspaceId === 'string' &&
              cachedWorkspaceId === currentWorkspaceId &&
              parsedInfo.name
            ) {
              // Use cached data immediately for better UX. Normalize through
              // `normalizeWorkspaceInfo` so legacy blobs missing
              // `onboardingCompleted` (pre-RET-796 sessions or entries written
              // before `Activate.helpers.ts` was updated) land in context with
              // the canonical boolean shape instead of `undefined`.
              setWorkspaceInfo(normalizeWorkspaceInfo(parsedInfo));
              setWorkspaceInfoWorkspaceId(currentWorkspaceId);

              // AGI-320: the cached copy resolves the role only when it lists
              // the caller. Otherwise nothing is written: the settlement stays
              // pending and the fetch below decides.
              const cachedUserRole = findListedRole(
                Object.values(parsedInfo.users),
                currentUserId,
              );
              if (cachedUserRole) {
                setCurrentUserRole(cachedUserRole);
                setUserRoleSettlement({
                  workspaceId: currentWorkspaceId,
                  isRoleResolved: true,
                });
              } else {
                LLog.warn(
                  {
                    component: LOG_COMPONENTS.API,
                    method: 'useEffect[currentWorkspaceId]',
                    operation: LOG_OPERATIONS.STORAGE_READ,
                  },
                  'Cached workspace info does not list the current user; the role waits for the fetch',
                  { workspaceId: currentWorkspaceId, userId: currentUserId },
                );
              }

              LLog.debug(
                {
                  component: LOG_COMPONENTS.API,
                  method: 'useEffect[currentWorkspaceId]',
                  operation: LOG_OPERATIONS.DEBUG,
                },
                'Using cached workspace info from localStorage',
                {
                  workspaceId: currentWorkspaceId,
                  userCount: Object.keys(parsedInfo.users).length,
                },
              );
            }
          } catch {
            // Invalid cached data, will fetch fresh
            LLog.warn(
              {
                component: LOG_COMPONENTS.API,
                method: 'useEffect[currentWorkspaceId]',
                operation: LOG_OPERATIONS.DEBUG,
              },
              'Failed to parse cached workspace info, fetching fresh',
            );
          }
        }

        // Always fetch fresh data from API to ensure consistency
        // This runs in parallel/after setting cached data
        const response =
          await WorkspaceService.getWorkspaceInfo(currentWorkspaceId);
        if (cancelled) return;

        const nextWorkspaceInfo = () => {
          const users = deduplicateUsers(response.users);

          // CRITICAL: If current user is logged in but backend still shows invitation_sent,
          // override the state to LINKED (backend might not have updated yet)
          if (currentUserId && users[currentUserId]) {
            const currentUserInWorkspace = users[currentUserId];
            const isInvitationState =
              currentUserInWorkspace.state === eUserStateType.INVITATION_SENT ||
              currentUserInWorkspace.state === eUserStateType.PENDING ||
              currentUserInWorkspace.state ===
                eUserStateType.INVITATION_EXPIRED;

            // If user is logged in (has session) but state is invitation, they must be LINKED
            if (isInvitationState) {
              users[currentUserId] = {
                ...currentUserInWorkspace,
                state: eUserStateType.LINKED,
              };
              LLog.debug(
                {
                  component: LOG_COMPONENTS.API,
                  method: 'useEffect[currentWorkspaceId]',
                  operation: LOG_OPERATIONS.DEBUG,
                },
                'Overriding user state from invitation to LINKED (user is logged in)',
                {
                  userId: currentUserId,
                  previousState: currentUserInWorkspace.state,
                },
              );
            }
          }

          const newWorkspaceInfo: tCurrentWorkspaceInfo = buildWorkspaceInfo(
            response,
            users,
          );
          return newWorkspaceInfo;
        };

        // AGI-320: the fresh list is the answer. A caller it does not name goes
        // back to the SELLER default the app boots with, and the lookup settles
        // unresolved, as a failed fetch does, so nothing takes SELLER for their
        // role. The reset drops any role an earlier cached copy gave: about 70
        // components read `currentUserRole` directly, without ACL's fail-closed
        // flags (CRisPR on #3906). bff-web answers 200 with an empty list when
        // workspace-service fails, and a member removed mid-session is no
        // longer listed.
        const listedRole = findListedRole(response.users, currentUserId);
        if (listedRole) {
          setCurrentUserRole(listedRole);
          setUserRoleSettlement({
            workspaceId: currentWorkspaceId,
            isRoleResolved: true,
          });
        } else {
          setCurrentUserRole(eUserRole.SELLER);
          setUserRoleSettlement({
            workspaceId: currentWorkspaceId,
            isRoleResolved: false,
          });
          LLog.warn(
            {
              component: LOG_COMPONENTS.API,
              method: 'useEffect[currentWorkspaceId]',
              operation: LOG_OPERATIONS.RESPONSE,
            },
            'Workspace user list does not include the current user; the role stays unresolved',
            {
              workspaceId: currentWorkspaceId,
              userId: currentUserId,
              userCount: response.users.length,
            },
          );
        }
        const freshWorkspaceInfo = nextWorkspaceInfo();
        setWorkspaceInfo(freshWorkspaceInfo);
        setWorkspaceInfoLoadedForId(currentWorkspaceId);
        setWorkspaceInfoWorkspaceId(currentWorkspaceId);

        // Update localStorage with fresh data for future use
        localStorage.setItem(
          'workspaceInfo',
          JSON.stringify({
            ...freshWorkspaceInfo,
            workspaceId: currentWorkspaceId,
          }),
        );

        await getAllWorkspaces();
      } catch (err) {
        if (cancelled) return;
        // The lookup settled even when it failed. A role already taken from the
        // cache for this workspace is kept; otherwise the role is the default.
        setUserRoleSettlement((settlement) =>
          settlement?.workspaceId === currentWorkspaceId
            ? settlement
            : { workspaceId: currentWorkspaceId, isRoleResolved: false },
        );
        addAlert({
          type: eAlertType.ERROR,
          message:
            (err as Error).message ||
            'Error al acceder al servidor para traer la información de tu workspace.',
        });
      } finally {
        // Clear the loading flag even on failure so consumers (AuthMiddleware)
        // don't block rendering forever when the fetch fails. `workspaceInfo`
        // may still be undefined after this runs; guards must handle that case
        // explicitly instead of treating "undefined" as "loading".
        if (!cancelled) setIsLoadingWorkspaceInfo(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U19 preserves the existing effect trigger (KTD4).
  }, [currentWorkspaceId]);

  useEffect(() => {
    if (
      !currentUserId ||
      isUndefined(currentUser) ||
      isEmpty(currentUser.workspaces)
    ) {
      setCurrentWorkspaceId(undefined);
      setCurrentWorkspace(undefined);
      return;
    }

    let workspace = currentUser.workspaces[0];
    let workspaceId = workspace.id;

    if (currentWorkspaceId) {
      const currentWorkspaceIndex = currentUser.workspaces.findIndex(
        (workspace: tCurrentWorkspace) => workspace.id === currentWorkspaceId,
      );

      if (currentWorkspaceIndex >= 0) {
        workspace = currentUser.workspaces[currentWorkspaceIndex];
        workspaceId = currentWorkspaceId;
      }
    }
    setCurrentWorkspaceId(workspaceId);
    setCurrentWorkspace(workspace);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U19 preserves the existing effect trigger (KTD4).
  }, [currentUserId, currentWorkspaceId, currentUser?.workspaces]);

  const handleWorkspaceUpdate = useCallback(
    (event: tWorkspaceUpdatedEvent) => {
      if (!isBoolean(event.workspace.active) || isUndefined(workspaceInfo)) {
        return;
      }
      // NOTE: this handler whitelists specific fields from the socket payload
      // (`active`, `settingUp`). If `tWorkspaceUpdatedEvent` is ever extended to
      // carry root-level `tCurrentWorkspaceInfo` fields (e.g., `onboardingCompleted`
      // on a backend-driven reset), add them here or the update will be silently
      // dropped. Mutation uses spread so unrelated fields remain preserved.
      setWorkspaceInfo((workspace) => {
        if (workspace === undefined) {
          return undefined;
        }
        return {
          ...workspace,
          isActive: event.workspace.active ?? false,
          settingUp: event.workspace.settingUp,
        };
      });
    },
    [workspaceInfo],
  );

  const handleWorkspaceUserUpdate = useCallback(
    (event: tWorkspaceUserUpdatedEvent) => {
      LLog.debug(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'handleWorkspaceUserUpdate',
          operation: LOG_OPERATIONS.DEBUG,
        },
        'Received workspace user update event',
        {
          eventType: event.eventType,
          userId: event.user.authId,
          state: event.user.state,
          hasWorkspaceInfo: !!workspaceInfo,
          workspaceId: event.workspace_id,
        },
      );

      // For 'activated' events, we should process even if workspaceInfo isn't loaded yet
      // This ensures real-time updates work even during initial load
      if (!workspaceInfo && event.eventType !== 'activated') {
        return;
      }

      // If workspaceInfo doesn't exist but we have an activated event, initialize it
      if (!workspaceInfo && event.eventType === 'activated') {
        // Try to fetch workspace info or wait for it to be loaded
        // For now, we'll just return and let the next workspaceInfo load handle it
        // But we should still try to process it if we have the workspace_id
        LLog.debug(
          {
            component: LOG_COMPONENTS.SOCKET,
            method: 'handleWorkspaceUserUpdate',
            operation: LOG_OPERATIONS.DEBUG,
          },
          'Received activated event but workspaceInfo not loaded yet, will be handled on next load',
          {
            workspaceId: event.workspace_id,
            userId: event.user.authId,
          },
        );
        return;
      }

      if (!workspaceInfo) {
        return;
      }

      const users: { [key: string]: tWorkspaceUserInfo } = {
        ...(workspaceInfo?.users ?? {}),
      };

      const user = event.user;

      if (event.eventType == 'update') {
        const existingUser = users[user.authId];
        // CRITICAL: If this user was recently manually updated (within last 5 seconds),
        // preserve the existing goal to prevent socket events with stale data from overwriting
        const wasRecentlyUpdated = recentlyUpdatedUsersRef.current.has(
          user.authId,
        );
        const updateTimestamp = recentlyUpdatedUsersRef.current.get(
          user.authId,
        );
        const fiveSecondsAgo = Date.now() - 5000;
        const isWithinCooldown =
          wasRecentlyUpdated &&
          updateTimestamp &&
          updateTimestamp > fiveSecondsAgo;

        // If user was recently manually updated, prefer existing goal over socket event goal
        // This prevents race conditions where backend sends stale goal data
        const goal =
          isWithinCooldown && existingUser?.goal !== undefined
            ? existingUser.goal // Use existing (manually updated) goal
            : user.goal !== undefined
              ? user.goal // Use goal from socket event
              : (existingUser?.goal ?? 0); // Fallback to existing or 0

        // Also protect role if user was recently manually updated
        const role =
          isWithinCooldown && existingUser?.role
            ? existingUser.role // Use existing (manually updated) role
            : (user.role ?? existingUser?.role ?? eUserRole.SELLER);

        // RET-1350: availability gets the same shield — the self-toggle writes
        // provider truth via updateWorkspaceUser, and a delayed socket event
        // from before the toggle must not revert the very field just changed.
        const isInactive =
          isWithinCooldown && existingUser?.isInactive !== undefined
            ? existingUser.isInactive
            : (user.is_inactive ?? false);

        users[user.authId] = {
          uuid: user.uuid,
          authId: user.authId,
          name: user.name,
          // Preserve existing firstName/lastName if not provided in event
          firstName: existingUser?.firstName ?? '',
          lastName: existingUser?.lastName ?? '',
          profilePicture:
            user.profilePicture ?? existingUser?.profilePicture ?? '',
          role: role,
          email: user.email,
          goal: goal,
          hasInboxActive: existingUser?.hasInboxActive ?? false,
          isInactive: isInactive,
          // Preserve createdAt to maintain sort order
          createdAt: existingUser?.createdAt,
          // State is critical - use from event if provided, otherwise preserve existing
          state: user.state ?? existingUser?.state,
        };
      } else if (event.eventType == 'assigned') {
        // New user assigned to workspace
        users[user.authId] = {
          uuid: user.uuid,
          authId: user.authId,
          name: user.name,
          firstName: '',
          lastName: '',
          profilePicture: user.profilePicture ?? '',
          role: user.role ?? eUserRole.SELLER,
          email: user.email,
          goal: user.goal ?? 0,
          hasInboxActive: false,
          isInactive: user.is_inactive ?? false,
          state: user.state,
        };
      } else if (event.eventType == 'activated') {
        // User completed registration - update state to linked
        // IMPORTANT: Look up by email first, as the authId might have changed
        // (invitation might have different authId than registered user)
        LLog.debug(
          {
            component: LOG_COMPONENTS.SOCKET,
            method: 'handleWorkspaceUserUpdate',
            operation: LOG_OPERATIONS.DEBUG,
          },
          'Received workspace.user.activated event',
          { event, user },
        );

        // First try to find by authId
        let existingUser = users[user.authId];
        let existingAuthId = user.authId;

        // If not found by authId, try to find by email (for invitation entries)
        // This handles the case where invitation entry has different authId than registered user
        if (!existingUser && user.email) {
          const emailLower = user.email.toLowerCase();
          // Search for existing user with same email but different authId
          // Prioritize invitation states, but also check any non-LINKED state
          for (const [authId, userData] of Object.entries(users)) {
            if (userData.email?.toLowerCase() === emailLower) {
              const isInvitationState =
                userData.state === eUserStateType.INVITATION_SENT ||
                userData.state === eUserStateType.PENDING ||
                userData.state === eUserStateType.INVITATION_EXPIRED;
              const isNotLinked = userData.state !== eUserStateType.LINKED;

              // If we find an invitation entry or any non-LINKED entry with same email, use it
              if (
                isInvitationState ||
                (isNotLinked && authId !== user.authId)
              ) {
                existingUser = userData;
                existingAuthId = authId;
                LLog.debug(
                  {
                    component: LOG_COMPONENTS.SOCKET,
                    method: 'handleWorkspaceUserUpdate',
                    operation: LOG_OPERATIONS.DEBUG,
                  },
                  'Found existing user by email, will update to LINKED',
                  {
                    existingAuthId: authId,
                    existingState: userData.state,
                    newAuthId: user.authId,
                    email: user.email,
                  },
                );
                break;
              }
            }
          }
        }

        if (existingUser) {
          // If authId changed (invitation -> registered), remove old entry and create new one
          if (existingAuthId !== user.authId) {
            delete users[existingAuthId];
            LLog.debug(
              {
                component: LOG_COMPONENTS.SOCKET,
                method: 'handleWorkspaceUserUpdate',
                operation: LOG_OPERATIONS.DEBUG,
              },
              'Removed old invitation entry, creating new LINKED entry',
              {
                oldAuthId: existingAuthId,
                newAuthId: user.authId,
              },
            );
          }

          // Update or create user with LINKED state
          // Use event data as primary source, fall back to existing data for missing fields
          users[user.authId] = {
            uuid: user.uuid ?? existingUser.uuid,
            authId: user.authId, // Always use new authId
            name: user.name ?? existingUser.name,
            firstName: existingUser.firstName ?? '', // Preserve firstName if available
            lastName: user.lastName ?? existingUser.lastName ?? '',
            profilePicture:
              user.profilePicture ?? existingUser.profilePicture ?? '',
            role: user.role ?? existingUser.role ?? eUserRole.SELLER,
            email: user.email ?? existingUser.email,
            goal: user.goal ?? existingUser.goal ?? 0,
            hasInboxActive: existingUser.hasInboxActive ?? false,
            isInactive: user.is_inactive ?? existingUser.isInactive ?? false,
            // CRITICAL: Always set state to LINKED when user activates
            state: eUserStateType.LINKED,
          };
          LLog.debug(
            {
              component: LOG_COMPONENTS.SOCKET,
              method: 'handleWorkspaceUserUpdate',
              operation: LOG_OPERATIONS.DEBUG,
            },
            'Updated user state to LINKED in handleWorkspaceUserUpdate (activated event)',
            {
              userId: user.authId,
              eventState: user.state,
              existingState: existingUser.state,
              finalState: users[user.authId].state,
            },
          );
        } else {
          // User doesn't exist yet, create them
          users[user.authId] = {
            uuid: user.uuid,
            authId: user.authId,
            name: user.name,
            firstName: '', // tWorkspaceUserUpdate doesn't have firstName
            lastName: user.lastName ?? '',
            profilePicture: user.profilePicture ?? '',
            role: user.role ?? eUserRole.SELLER,
            email: user.email,
            goal: user.goal ?? 0,
            hasInboxActive: false,
            isInactive: user.is_inactive ?? false,
            state: user.state ?? eUserStateType.LINKED,
          };
          LLog.debug(
            {
              component: LOG_COMPONENTS.SOCKET,
              method: 'handleWorkspaceUserUpdate',
              operation: LOG_OPERATIONS.DEBUG,
            },
            'Created new user with LINKED state in handleWorkspaceUserUpdate (activated event)',
            {
              userId: user.authId,
              finalState: users[user.authId].state,
            },
          );
        }
      } else if (event.eventType == 'invited') {
        // User was invited - update state to INVITATION_SENT
        // Note: invited events have a different structure with pendingUser
        LLog.debug(
          {
            component: LOG_COMPONENTS.SOCKET,
            method: 'handleWorkspaceUserUpdate',
            operation: LOG_OPERATIONS.DEBUG,
          },
          'Received workspace.user.invited event',
          { event },
        );
        // For invited events, check if we have user data directly or in pendingUser
        // The invited event structure is different - it may have pendingUser with snake_case properties
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const invitedEvent = event as any;
        const pendingUserData = invitedEvent.pendingUser?.user;
        const userToUpdate = user || pendingUserData;

        if (userToUpdate) {
          // Handle both camelCase (from user) and snake_case (from pendingUser) properties
          const userId =
            userToUpdate.authId ||
            (pendingUserData?.auth_id as string | undefined);
          if (userId) {
            const existingUser = users[userId];
            if (existingUser) {
              users[userId] = {
                ...existingUser,
                state: eUserStateType.INVITATION_SENT,
              };
            } else {
              // Create new user with INVITATION_SENT state
              // Handle both camelCase (from user) and snake_case (from pendingUser) property names
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const pendingUser = pendingUserData as any;
              const firstName = pendingUser?.first_name || '';
              const lastName = pendingUser?.last_name || '';
              const profilePicture = pendingUser?.profile_picture || '';

              users[userId] = {
                uuid: userToUpdate.uuid || pendingUser?.uuid || '',
                authId: userId,
                name: userToUpdate.name || pendingUser?.name || '',
                firstName,
                lastName,
                profilePicture,
                role:
                  userToUpdate.role ||
                  (pendingUser?.role as eUserRole) ||
                  eUserRole.SELLER,
                email: userToUpdate.email || pendingUser?.email || '',
                goal: userToUpdate.goal || pendingUser?.goal || 0,
                hasInboxActive: false,
                isInactive:
                  userToUpdate.is_inactive ?? pendingUser?.is_inactive ?? false,
                state: eUserStateType.INVITATION_SENT,
              };
            }
          }
        }
      } else if (event.eventType == 'unassigned') {
        delete users[user.authId];
      }

      // Apply deduplication to ensure no duplicate entries by email
      // This is especially important for 'activated' events where we might have
      // both invitation and activated entries
      const deduplicatedUsers = deduplicateUsers(Object.values(users));

      setWorkspaceInfo((workspace) => {
        if (workspace === undefined) {
          return undefined;
        }
        return {
          ...workspace,
          users: deduplicatedUsers,
        };
      });
    },
    [deduplicateUsers, workspaceInfo],
  );

  const handleWorkspaceUserUpdated = useCallback(
    (event: tWorkspaceUsersUpdatedEvent) => {
      // BUG-534 U4: ahooks setter (was raw `localStorage.setItem`). The pure
      // merger lives in `Workspace.helpers.ts` so the behavior is unit-tested
      // without dragging in the full Workspace.hook dependency graph. Round-2
      // A4 verified no production consumer reads role/uuid/autoAssignable off
      // `funnelData[].users[]`, so narrowing to `{auth_id, name}` is safe.
      setFunnelData((prev) =>
        applyWorkspaceUsersUpdateToFunnelData(prev, event.users),
      );

      // Start with existing users to preserve all users not in the event
      const existingUsers: { [key: string]: tWorkspaceUserInfo } = {
        ...(workspaceInfo?.users ?? {}),
      };

      // Convert event users to tWorkspaceUserInfo format
      const eventUsersArray: tWorkspaceUserInfo[] = event.users
        .filter((user) => user.id && user.email)
        .map((user) => {
          const existingUser = existingUsers[user.id];
          // CRITICAL: If this user was recently manually updated (within last 5 seconds),
          // preserve the existing goal to prevent socket events with stale data from overwriting
          const wasRecentlyUpdated = recentlyUpdatedUsersRef.current.has(
            user.id,
          );
          const updateTimestamp = recentlyUpdatedUsersRef.current.get(user.id);
          const fiveSecondsAgo = Date.now() - 5000;
          const isWithinCooldown =
            wasRecentlyUpdated &&
            updateTimestamp &&
            updateTimestamp > fiveSecondsAgo;

          // If user was recently manually updated, prefer existing goal over socket event goal
          // This prevents race conditions where backend sends stale goal data
          const goal =
            isWithinCooldown && existingUser?.goal !== undefined
              ? existingUser.goal // Use existing (manually updated) goal
              : user.goal !== undefined
                ? user.goal // Use goal from socket event
                : (existingUser?.goal ?? 0); // Fallback to existing or 0

          // Also protect role if user was recently manually updated
          const role =
            isWithinCooldown && existingUser?.role
              ? existingUser.role // Use existing (manually updated) role
              : (user.role ?? existingUser?.role ?? eUserRole.SELLER);

          // RET-1350: availability gets the same shield — the self-toggle
          // writes provider truth via updateWorkspaceUser, and a delayed
          // socket event must not revert the very field just changed.
          const isInactive =
            isWithinCooldown && existingUser?.isInactive !== undefined
              ? existingUser.isInactive
              : (user.is_inactive ?? false);

          return {
            uuid: user.uuid,
            authId: user.id,
            name: user.name,
            firstName: existingUser?.firstName ?? '',
            lastName: existingUser?.lastName ?? '',
            profilePicture:
              user.profilePicture ?? existingUser?.profilePicture ?? '',
            role: role,
            email: user.email,
            goal: goal,
            hasInboxActive: existingUser?.hasInboxActive ?? false,
            isInactive: isInactive,
            state: user.state ?? existingUser?.state,
          };
        });

      // Merge existing users with event users
      // CRITICAL: Event users should replace existing users, not merge with them
      // This ensures that only users in the event are updated, preserving others
      const allUsersArray: tWorkspaceUserInfo[] = [
        ...Object.values(existingUsers).filter(
          (user) => !eventUsersArray.find((eu) => eu.authId === user.authId),
        ),
        ...eventUsersArray,
      ];

      // Apply deduplication to the merged array
      // This ensures that if both invitation and activated entries are in the event,
      // only the LINKED one is kept
      const deduplicatedUsers = deduplicateUsers(allUsersArray);

      setWorkspaceInfo((workspace) => {
        if (workspace === undefined) {
          return undefined;
        }
        return {
          ...workspace,
          users: deduplicatedUsers,
        };
      });
    },
    [deduplicateUsers, setFunnelData, workspaceInfo?.users],
  );

  const onCreateWorkspaceSubmit = useCallback(
    async (
      formData: tCreateWorkspaceModalForm,
    ): Promise<tWorkspaceResponse> => {
      try {
        // Stale (>90d) or absent attribution is omitted entirely — no
        // 'register'-style fallback on this rail (NULL means unattributed).
        const attribution = isAttributionFresh(workspaceAttribution)
          ? workspaceAttribution
          : undefined;

        const request: tWorkspaceRequest = {
          name: formData.companyName,
          countryCode: formData.countryCode,
          industryType: Number(formData.companyType),
          phoneNumber: formData.phoneNumber,
          salesSource: workspaceSaleSource ?? 'register',
          useIndustryTemplate: import.meta.env.VITE_ENABLE_TEMPLATES === 'true',
          ...(attribution && { attribution }),
        };
        const response = await WorkspaceService.createWorkspace(request);

        // Registration-scoped: clear ONLY after the backend confirmed the write
        // (write-after-confirm learning). A failed creation keeps the stash so
        // the retry still carries attribution. The salesSource rail (including
        // its Pricing_LPprecios clear branch) is deliberately untouched.
        if (attribution) {
          setWorkspaceAttribution(undefined);
        }

        setCurrentWorkspaceId(response.data.id);
        setCurrentWorkspace(response.data);
        if (!isEmpty(currentUser)) {
          setCurrentUser((prevUser: tSessionUser | undefined) => {
            if (!prevUser) return prevUser;
            return {
              ...prevUser,
              workspaces: [...(prevUser?.workspaces ?? []), response.data],
            };
          });
        }
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [
      currentUser,
      setCurrentUser,
      setCurrentWorkspace,
      setCurrentWorkspaceId,
      setWorkspaceAttribution,
      workspaceAttribution,
      workspaceSaleSource,
    ],
  );

  const updateWorkspaceInfo = useCallback(
    async (props: tUpdateWorkspaceProps): Promise<tUpdateWorkspaceResponse> => {
      const nextWorkspaceInfo = (
        workspace: tCurrentWorkspaceInfo | undefined,
      ) => {
        if (workspace === undefined) {
          return undefined;
        }
        const nextWorkspaceInfo = {
          ...workspace,
          ...(props.companyName !== undefined && { name: props.companyName }),
          ...(props.phoneNumber !== undefined && {
            phoneNumber: props.phoneNumber,
          }),
        };
        return nextWorkspaceInfo;
      };

      const savedForWorkspaceId = currentWorkspaceId;
      try {
        const response = await WorkspaceService.updateWorkspace(
          {
            ...(props.companyName !== undefined && {
              company_name: props.companyName,
            }),
            ...(props.phoneNumber !== undefined && {
              phone_number: props.phoneNumber,
            }),
          },
          currentWorkspaceId || '',
        );
        setWorkspaceInfo(nextWorkspaceInfo);
        // The next load reads this blob before the fetch answers, so a stale name
        // here would send the old name to analytics first. The change is merged
        // into the stored blob, not into this render's state: the fetch writes the
        // blob in the same tick it sets the state, so the blob is the newest copy.
        if (latestCurrentWorkspaceIdRef.current === savedForWorkspaceId) {
          try {
            const cachedWorkspaceInfo = JSON.parse(
              localStorage.getItem('workspaceInfo') ?? 'null',
            );
            if (cachedWorkspaceInfo?.workspaceId === savedForWorkspaceId) {
              localStorage.setItem(
                'workspaceInfo',
                JSON.stringify(nextWorkspaceInfo(cachedWorkspaceInfo)),
              );
            }
          } catch {
            // The save already committed. Without the blob the next load waits
            // for the fetch instead of reading the old name.
            try {
              localStorage.removeItem('workspaceInfo');
            } catch {
              // Storage is unavailable; there is no blob to read either.
            }
            LLog.warn(
              {
                component: LOG_COMPONENTS.API,
                method: 'updateWorkspaceInfo',
                operation: LOG_OPERATIONS.DEBUG,
              },
              'Failed to cache workspace info after save',
            );
          }
        }
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [currentWorkspaceId],
  );

  const addUserToWorkspace = useCallback(
    async (
      payload: tAddWorkspaceUserPayload,
    ): Promise<tAddWorkspaceUserResponse> => {
      try {
        const userAdditionResponse = await WorkspaceService.addWorkspaceUser(
          payload,
          currentWorkspaceId ?? '',
        );

        // Refresh workspace info to get the latest state from backend
        // This ensures we have the correct state (PENDING, INVITATION_SENT, etc.)
        if (currentWorkspaceId) {
          try {
            const workspaceResponse =
              await WorkspaceService.getWorkspaceInfo(currentWorkspaceId);
            const nextWorkspaceInfo = () => {
              const users = deduplicateUsers(workspaceResponse.users);

              // CRITICAL: If current user is logged in but backend still shows invitation_sent,
              // override the state to LINKED (backend might not have updated yet)
              if (currentUserId && users[currentUserId]) {
                const currentUserInWorkspace = users[currentUserId];
                const isInvitationState =
                  currentUserInWorkspace.state ===
                    eUserStateType.INVITATION_SENT ||
                  currentUserInWorkspace.state === eUserStateType.PENDING ||
                  currentUserInWorkspace.state ===
                    eUserStateType.INVITATION_EXPIRED;

                // If user is logged in (has session) but state is invitation, they must be LINKED
                if (isInvitationState) {
                  users[currentUserId] = {
                    ...currentUserInWorkspace,
                    state: eUserStateType.LINKED,
                  };
                }
              }

              const newWorkspaceInfo: tCurrentWorkspaceInfo =
                buildWorkspaceInfo(workspaceResponse, users);
              return newWorkspaceInfo;
            };

            setWorkspaceInfo(nextWorkspaceInfo());
          } catch {
            // If refresh fails, fall back to optimistic update
            const nextWorkspaceInfo = (
              workspace: tCurrentWorkspaceInfo | undefined,
            ) => {
              if (workspace === undefined) {
                return undefined;
              }

              const nextWorkspaceUsersInfo = {
                ...workspace,
                users: {
                  ...workspace.users,
                  [userAdditionResponse.auth_id]: {
                    uuid: userAdditionResponse.userArray.uuid,
                    authId: userAdditionResponse.auth_id,
                    name: userAdditionResponse.userArray.name,
                    firstName: userAdditionResponse.userArray.first_name,
                    lastName: userAdditionResponse.userArray.last_name ?? '',
                    profilePicture:
                      userAdditionResponse.userArray.profile_picture ?? '',
                    role: payload.role,
                    email: payload.email,
                    goal: userAdditionResponse.userArray.goal,
                    hasInboxActive: false,
                    // For userManagementV2, new users should have PENDING or INVITATION_SENT state
                    state: workspace.userManagementV2
                      ? eUserStateType.INVITATION_SENT
                      : undefined,
                  },
                },
              };
              return nextWorkspaceUsersInfo;
            };

            setWorkspaceInfo(nextWorkspaceInfo);
          }
        }

        return Promise.resolve(userAdditionResponse);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [currentUserId, currentWorkspaceId, deduplicateUsers],
  );

  const resendUserInvitation = useCallback(
    async (
      payload: tResendInvitationPayload,
    ): Promise<tResendInvitationResponse> => {
      try {
        const response = await WorkspaceService.resendUserInvitation(payload);

        // Refresh workspace info to get updated user state
        if (currentWorkspaceId) {
          const workspaceResponse =
            await WorkspaceService.getWorkspaceInfo(currentWorkspaceId);
          const nextWorkspaceInfo = () => {
            const users = deduplicateUsers(workspaceResponse.users);

            // CRITICAL: If current user is logged in but backend still shows invitation_sent,
            // override the state to LINKED (backend might not have updated yet)
            if (currentUserId && users[currentUserId]) {
              const currentUserInWorkspace = users[currentUserId];
              const isInvitationState =
                currentUserInWorkspace.state ===
                  eUserStateType.INVITATION_SENT ||
                currentUserInWorkspace.state === eUserStateType.PENDING ||
                currentUserInWorkspace.state ===
                  eUserStateType.INVITATION_EXPIRED;

              // If user is logged in (has session) but state is invitation, they must be LINKED
              if (isInvitationState) {
                users[currentUserId] = {
                  ...currentUserInWorkspace,
                  state: eUserStateType.LINKED,
                };
              }
            }

            const newWorkspaceInfo: tCurrentWorkspaceInfo = buildWorkspaceInfo(
              workspaceResponse,
              users,
            );
            return newWorkspaceInfo;
          };

          setWorkspaceInfo(nextWorkspaceInfo());
        }

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [currentUserId, currentWorkspaceId, deduplicateUsers],
  );

  const updateWorkspaceUserInbox = useCallback(
    async (
      userAuthId: string,
      payload: tToggleUserInboxPayload,
    ): Promise<void> => {
      const nextWorkspaceInfo = (
        workspace: tCurrentWorkspaceInfo | undefined,
      ) => {
        if (!workspace) {
          return undefined;
        }

        const nextWorkspaceUserInboxState: tCurrentWorkspaceInfo = {
          ...workspace,
          users: {
            ...workspace.users,
            [userAuthId]: {
              ...workspace.users[userAuthId],
              hasInboxActive: payload.can_inbox,
            },
          },
        };

        return nextWorkspaceUserInboxState;
      };

      if (isUndefined(currentWorkspaceId)) {
        return Promise.reject('Workspace ID is not defined');
      }
      try {
        setWorkspaceInfo(nextWorkspaceInfo);
        await WorkspaceService.toggleWorkspaceUserInbox(
          currentWorkspaceId,
          userAuthId,
          payload,
        );
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [currentWorkspaceId],
  );

  const deleteWorkspaceUser = useCallback(
    async (userToDeleteId: string) => {
      if (isUndefined(currentWorkspaceId)) {
        return;
      }
      await WorkspaceService.deleteWorkspaceUser(
        currentWorkspaceId,
        userToDeleteId,
      );

      // Refresh workspace info to get the latest state from backend
      // This ensures all remaining users have their correct state
      try {
        const workspaceResponse =
          await WorkspaceService.getWorkspaceInfo(currentWorkspaceId);
        const nextWorkspaceInfo = () => {
          const users = deduplicateUsers(workspaceResponse.users);
          const newWorkspaceInfo: tCurrentWorkspaceInfo = buildWorkspaceInfo(
            workspaceResponse,
            users,
          );
          return newWorkspaceInfo;
        };

        setWorkspaceInfo(nextWorkspaceInfo());
      } catch {
        // If refresh fails, fall back to optimistic update
        setWorkspaceInfo((info) => {
          if (!info) {
            return info;
          }
          delete info.users[userToDeleteId];
          return {
            ...info,
            users: info.users,
          };
        });
      }
    },
    [currentWorkspaceId, deduplicateUsers],
  );

  const unlinkWorkspaceUser = useCallback(
    async (userToUnlinkId: string, payload: tUnlinkUserWorkspacePayload) => {
      if (isUndefined(currentWorkspaceId)) {
        return;
      }
      await WorkspaceService.unlinkWorkspaceUser(
        currentWorkspaceId,
        userToUnlinkId,
        payload,
      );

      // Refresh workspace info to get the latest state from backend
      // This ensures all remaining users have their correct state
      try {
        const workspaceResponse =
          await WorkspaceService.getWorkspaceInfo(currentWorkspaceId);
        const nextWorkspaceInfo = () => {
          const users = deduplicateUsers(workspaceResponse.users);
          const newWorkspaceInfo: tCurrentWorkspaceInfo = buildWorkspaceInfo(
            workspaceResponse,
            users,
          );
          return newWorkspaceInfo;
        };

        setWorkspaceInfo(nextWorkspaceInfo());
      } catch {
        // If refresh fails, fall back to optimistic update
        setWorkspaceInfo((info) => {
          if (!info) {
            return info;
          }
          return {
            ...info,
            users: {
              ...info.users,
              [userToUnlinkId]: {
                ...info.users[userToUnlinkId],
                state: eUserStateType.UNLINKING,
              },
            },
          };
        });
      }
    },
    [currentWorkspaceId, deduplicateUsers],
  );

  const updateWorkspaceUser = useCallback((updatedUser: tWorkspaceUserInfo) => {
    // Mark this user as recently manually updated to prevent socket events from overwriting
    // This prevents race conditions where socket events arrive with stale data
    recentlyUpdatedUsersRef.current.set(updatedUser.authId, Date.now());

    // Clean up old entries (older than 10 seconds) to prevent memory leak
    const tenSecondsAgo = Date.now() - 10000;
    for (const [
      authId,
      timestamp,
    ] of recentlyUpdatedUsersRef.current.entries()) {
      if (timestamp < tenSecondsAgo) {
        recentlyUpdatedUsersRef.current.delete(authId);
      }
    }

    const nextWorkspaceInfo = (
      workspace: tCurrentWorkspaceInfo | undefined,
    ) => {
      if (workspace === undefined) {
        return undefined;
      }

      const nextWorkspaceUsersInfo = {
        ...workspace,
        users: {
          ...workspace.users,
          [updatedUser.authId]: updatedUser,
        },
      };
      return nextWorkspaceUsersInfo;
    };

    setWorkspaceInfo(nextWorkspaceInfo);
  }, []);

  const checkIfWorkspaceHasMoreThan90Days = useCallback((): boolean => {
    if (!workspaceInfo || !workspaceInfo.createdAt) return false;
    const createdAt = new Date(workspaceInfo.createdAt);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - createdAt.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 90;
  }, [workspaceInfo]);

  const getAllWorkspaces = useCallback(async () => {
    try {
      const response = await WorkspaceService.getWorkspaces();

      setWorkspaces(response);
      setCurrentUser((prevUser: tSessionUser | undefined) => {
        if (!prevUser) return prevUser;
        const uniqueWorkspaces = new Map();

        prevUser.workspaces?.forEach((workspace) => {
          uniqueWorkspaces.set(workspace.id, workspace);
        });

        response.workspaces.forEach((workspace) => {
          uniqueWorkspaces.set(workspace.id, workspace);
        });

        return {
          ...prevUser,
          workspaces: Array.from(uniqueWorkspaces.values()),
        };
      });
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }, [setCurrentUser]);

  const activateWorkspace = useCallback(() => {
    setWorkspaceInfo((prevWorkspaceInfo) => {
      if (prevWorkspaceInfo === undefined) {
        return undefined;
      }
      return { ...prevWorkspaceInfo, isActive: true };
    });
  }, []);

  /*
    RET-2339. La excepción de acceso de WBS sabe para qué workspace cargó.
    - La ref guarda el workspace de la ÚLTIMA petición. Una respuesta de otro
      workspace llega tarde y se descarta entera (valor y marca), como
      `getSummary` en `Subscription.hook` desde RET-1068.
    - La marca se pone solo cuando la consulta RESPONDE, no cuando termina:
      un fallo deja el acceso sin resolver, nunca "sin acceso". Por eso esto
      se aparta a propósito de la regla de "marcar al terminar" del CLAUDE.md.
    - `canSeeWapiIntegration` no cambia de significado ni se reinicia al
      cambiar de workspace; la marca derivada es la que dice si vale para
      este workspace.
  */
  useEffect(() => {
    wapiAccessRequestWorkspaceIdRef.current = currentWorkspaceId;
    const requestedWorkspaceId = currentWorkspaceId;
    (async () => {
      try {
        if (!requestedWorkspaceId) {
          return;
        }
        const response =
          await WorkspaceService.getWidAccessToWaApi(requestedWorkspaceId);
        if (wapiAccessRequestWorkspaceIdRef.current !== requestedWorkspaceId) {
          return;
        }
        setCanSeeWapiIntegration(response.active);
        setCanSendBulkMessages(response.massive_messages);
        // Una respuesta sin `active` booleano no es una respuesta sobre el
        // acceso: no puede convertirse en "sin acceso confirmado".
        if (isBoolean(response?.active)) {
          setWapiAccessLoadedForWorkspaceId(requestedWorkspaceId);
        }
      } catch (error) {
        console.error('Error fetching WAPI integration access:', error);
      }
    })();
  }, [currentWorkspaceId]);

  const hasLoadedWapiAccessException =
    !!currentWorkspaceId &&
    wapiAccessLoadedForWorkspaceId === currentWorkspaceId;

  // AGI-320: whether `currentUserRole` came from this workspace's own list.
  const roleResolved = isRoleResolved(userRoleSettlement, currentWorkspaceId);

  const getDisplayIntegration = useCallback(
    (
      chatIntegration: eChannelType | undefined,
      defaultChannel: eChannelType,
    ): eChannelType => {
      return chatIntegration ?? defaultChannel;
    },
    [],
  );

  return useMemo(
    () => ({
      currentWorkspaceId,
      setCurrentWorkspaceId,
      currentWorkspace,
      setCurrentWorkspace,
      onCreateWorkspaceSubmit,
      handleWorkspaceUpdate,
      workspaceInfo,
      isLoadingWorkspaceInfo,
      workspaceInfoLoadedForId,
      isRoleResolved: roleResolved,
      updateWorkspaceInfo,
      addUserToWorkspace,
      resendUserInvitation,
      handleWorkspaceUserUpdated,
      handleWorkspaceUserUpdate,
      updateWorkspaceUserInbox,
      deleteWorkspaceUser,
      unlinkWorkspaceUser,
      updateWorkspaceUser,
      checkIfWorkspaceHasMoreThan90Days,
      getAllWorkspaces,
      workspaces,
      setWorkspaces,
      setWorkspaceSaleSource,
      workspaceSaleSource,
      canSeeWapiIntegration,
      hasLoadedWapiAccessException,
      canSendBulkMessages,
      activateWorkspace,
      getDisplayIntegration,
    }),
    [
      activateWorkspace,
      addUserToWorkspace,
      canSeeWapiIntegration,
      canSendBulkMessages,
      checkIfWorkspaceHasMoreThan90Days,
      currentWorkspace,
      currentWorkspaceId,
      deleteWorkspaceUser,
      getAllWorkspaces,
      getDisplayIntegration,
      handleWorkspaceUpdate,
      handleWorkspaceUserUpdate,
      handleWorkspaceUserUpdated,
      hasLoadedWapiAccessException,
      isLoadingWorkspaceInfo,
      onCreateWorkspaceSubmit,
      resendUserInvitation,
      roleResolved,
      setCurrentWorkspace,
      setCurrentWorkspaceId,
      setWorkspaceSaleSource,
      unlinkWorkspaceUser,
      updateWorkspaceInfo,
      updateWorkspaceUser,
      updateWorkspaceUserInbox,
      workspaceInfo,
      workspaceInfoLoadedForId,
      workspaceSaleSource,
      workspaces,
    ],
  );
}

export default useWorkspace;
